import { useState, useEffect, useCallback } from 'react';
import mixpanel from 'mixpanel-browser';
import { getGooglePlaceDetails, parseAddressComponents, validateForm } from '../utils';
import { createUnifyShop, registerEmailInHubspot } from '@services/unify';
import { getAuthSession, switchToOrganization } from '@services/auth';
import { useAuthentication } from './useAuthentication';

const DRAFT_STORAGE_KEY = 'unify-onboarding-draft';

const isValidEmail = (email) => {
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(email);
};

const clearDraftFromLocalStorage = () => {
	try {
		localStorage.removeItem(DRAFT_STORAGE_KEY);
	} catch (err) {
		console.error('Failed to clear draft from localStorage:', err);
	}
};

export const useBusinessDetails = (user, plan) => {
	const [businessDetailsExpanded, setBusinessDetailsExpanded] = useState(false);
	const [businessDetailsCompleted, setBusinessDetailsCompleted] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [selectedPlace, setSelectedPlace] = useState(null);
	const [addressDetails, setAddressDetails] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [validationErrors, setValidationErrors] = useState(null);
	const [touchedFields, setTouchedFields] = useState({});
	const [showPassword, setShowPassword] = useState(false);
	const [storedPlan, setStoredPlan] = useState(plan);
	const [formData, setFormData] = useState({
		name: '',
		email: '',
		password: '',
		termsAccepted: false
	});

	const { isSignedIn, isAuthenticating, authError, handleGoogleSignUp, handleEmailPasswordSignUp, clearAuthError } = useAuthentication();

	// Update stored plan when URL parameter changes
	useEffect(() => {
		setStoredPlan(plan);
	}, [plan]);

	// Show password field when email is valid
	useEffect(() => {
		if (formData.email && isValidEmail(formData.email) && !showPassword && !isSignedIn) {
			setShowPassword(true);
			// Register email in HubSpot when password field appears
			registerEmailInHubspot(formData.email, storedPlan);
		}
	}, [formData.email, showPassword, isSignedIn, storedPlan]);

	const handlePlaceSelect = async (place) => {
		setSelectedPlace(place);
		setError(null);

		if (!place) {
			setAddressDetails(null);
			setBusinessDetailsExpanded(false);
			return;
		}

		// Track place selection
		mixpanel.track('Unify place selected', {
			place_name: place.label
		});

		try {
			const googlePlaceId = place.value.place_id;
			const placeDetails = await getGooglePlaceDetails(googlePlaceId);
			const parsedAddress = parseAddressComponents(placeDetails, googlePlaceId);
			setAddressDetails(parsedAddress);
			// Mark address-related fields as touched
			setTouchedFields((prev) => ({
				...prev,
				address: true,
				postcode: true,
				country: true
			}));
			// Only expand if step 1 hasn't been completed yet
			if (!businessDetailsCompleted) {
				setBusinessDetailsExpanded(true);
			}
		} catch (err) {
			console.error('Error fetching place details:', err);
			setError('Failed to fetch location details. Please try again.');
		}
	};

	const handleFormChange = (field, value) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		setError(null);
		// Clear auth error when user makes changes
		if (authError) {
			clearAuthError();
		}
	};

	const handleFieldBlur = (field) => {
		setTouchedFields((prev) => ({ ...prev, [field]: true }));
	};

	const handleEmailBlur = useCallback(() => {
		handleFieldBlur('email');
		if (formData.email && isValidEmail(formData.email)) {
			// Fire and forget - register email in HubSpot
			registerEmailInHubspot(formData.email, storedPlan);
		}
	}, [formData.email, storedPlan]);

	// Validation that only shows errors for touched fields
	useEffect(() => {
		if (!loading && !isAuthenticating && Object.keys(touchedFields).length > 0) {
			const allValidationErrors = validateForm(formData, addressDetails);

			// Only show errors for fields that have been touched
			const filteredErrors = {};
			Object.keys(allValidationErrors).forEach((field) => {
				if (touchedFields[field]) {
					filteredErrors[field] = allValidationErrors[field];
				}
			});

			if (Object.keys(filteredErrors).length > 0) {
				setValidationErrors(filteredErrors);
			} else {
				setValidationErrors(null);
			}
		}
	}, [formData, addressDetails, touchedFields, loading, isAuthenticating]);

	const handleGetStarted = async () => {
		setError(null);
		clearAuthError();
		setValidationErrors(null);

		// Track shop creation started
		mixpanel.track('Unify shop creation started', {
			plan: storedPlan
		});

		// Step 1: Authenticate if not signed in
		let userId;
		if (!isSignedIn) {
			// Create Clerk user account
			const authResult = await handleEmailPasswordSignUp(formData.email, formData.password, formData.name);

			userId = authResult.userId;

			if (!authResult.success) {
				// Track auth error
				mixpanel.track('Unify auth error', {
					error_message: 'Authentication failed'
				});
				// Error is already set in authError by useAuthentication
				// Clear validation errors so only auth error shows
				setValidationErrors(null);
				return;
			}

			// handleEmailPasswordSignUp signs the user in immediately — the
			// Unify Clerk app doesn't gate sign-up on email verification —
			// so continue straight to shop creation as the authenticated user.
		} else {
			// Get user ID from Clerk session
			const authSession = await getAuthSession();
			userId = authSession.userId;
		}

		// Step 2: Validate form
		const formValidationErrors = validateForm(formData, addressDetails);
		if (Object.keys(formValidationErrors).length > 0) {
			// Track validation error
			const firstErrorField = Object.keys(formValidationErrors)[0];
			mixpanel.track('Unify validation error', {
				field: firstErrorField,
				error_type: 'validation'
			});
			// Set validation errors to be displayed above Get started button
			setValidationErrors(formValidationErrors);
			return;
		}

		// Step 3: Create shop
		setLoading(true);
		await window.Clerk.user.reload();

		try {
			const shopData = {
				name: addressDetails.name,
				contactName: formData.name,
				contactEmail: formData.email,
				addressLine1: addressDetails.addressLine1,
				addressLine2: addressDetails.addressLine2 || undefined,
				city: addressDetails.city || undefined,
				postcode: addressDetails.postcode,
				country: addressDetails.country,
				publicPhoneNumber: addressDetails.publicPhoneNumber || undefined,
				googlePlaceId: addressDetails.googlePlaceId,
				geolocation: addressDetails.geolocation,
				openingHours: addressDetails.openingHours || undefined,
				types: addressDetails.types,
				userId,
				plan: storedPlan
			};

			const result = await createUnifyShop(shopData);

			// Reload user and poll until organisation membership appears
			let clerkOrgId = null;
			let retries = 10; // Try for up to 5 seconds (10 * 500ms)

			while (retries > 0 && !clerkOrgId) {
				await window.Clerk.user.reload();
				const orgs = window.Clerk.user.organizationMemberships || [];

				if (orgs.length > 0) {
					// Use the Clerk organization ID from the membership
					clerkOrgId = orgs[0].organization.id;
					break;
				}

				if (retries > 1) {
					await new Promise((resolve) => setTimeout(resolve, 500));
				}
				retries--;
			}

			// Switch to organisation using Clerk org ID
			if (clerkOrgId) {
				try {
					await switchToOrganization(clerkOrgId);
				} catch (err) {
					console.error('Failed to switch to organization:', err);
				}
			} else {
				console.error('Failed to find organization membership after shop creation');
			}

			// Step 5: Mark complete and clear draft
			setBusinessDetailsCompleted(true);
			setBusinessDetailsExpanded(false);
			setIsEditing(false);

			clearDraftFromLocalStorage();

			return result.shop;
		} catch (err) {
			console.error('Error creating shop:', err);
			setError(err.response?.data?.message || 'Failed to create shop. Please try again.');
			throw err;
		} finally {
			setLoading(false);
		}
	};

	const handleChangeStore = () => {
		setSelectedPlace(null);
		setAddressDetails(null);
		setBusinessDetailsExpanded(false);
	};

	const handleEdit = () => {
		setIsEditing(true);
		setBusinessDetailsCompleted(false);
		setBusinessDetailsExpanded(true);
	};

	const handleCancel = () => {
		setIsEditing(false);
		setBusinessDetailsCompleted(true);
		setBusinessDetailsExpanded(false);
	};

	const handleReset = useCallback(() => {
		setBusinessDetailsCompleted(false);
		setBusinessDetailsExpanded(false);
		setSelectedPlace(null);
		setAddressDetails(null);
		setShowPassword(false);
		setFormData({
			name: '',
			email: '',
			password: '',
			termsAccepted: false
		});
		setError(null);
		setValidationErrors(null);
		setTouchedFields({});
		clearDraftFromLocalStorage();
	}, []);

	// Reset state when user signs out
	useEffect(() => {
		if (!isSignedIn && businessDetailsCompleted) {
			handleReset();
		}
	}, [isSignedIn, businessDetailsCompleted, handleReset]);

	return {
		businessDetailsExpanded,
		businessDetailsCompleted,
		isEditing,
		selectedPlace,
		addressDetails,
		loading,
		error,
		validationErrors,
		formData,
		isSignedIn,
		showPassword,
		authError,
		isAuthenticating,
		setBusinessDetailsCompleted,
		setAddressDetails,
		setFormData,
		handlePlaceSelect,
		handleFormChange,
		handleFieldBlur,
		handleGetStarted,
		handleChangeStore,
		handleEdit,
		handleCancel,
		handleEmailBlur
	};
};
